import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { Calendar, Download, Search, Filter } from 'lucide-react';
import Button from '../components/ui/Button';
import { motion } from 'framer-motion';

// Typdefinition für einen Termin
interface Termin {
  id: string;
  email: string;
  phone?: string;
  topic: string;
  date: string;
  createdAt: string;
  status: 'neu' | 'bestätigt' | 'abgeschlossen' | 'storniert';
}

const TerminVerwaltung: React.FC = () => {
  const [termine, setTermine] = useState<Termin[]>([]);
  const [filteredTermine, setFilteredTermine] = useState<Termin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState({
    status: 'alle',
    topic: 'alle',
    searchTerm: '',
    startDate: '',
    endDate: '',
  });

  // Statistiken
  const [stats, setStats] = useState({
    total: 0,
    neu: 0,
    bestätigt: 0,
    abgeschlossen: 0,
    storniert: 0,
  });
  
  // Animation Varianten für Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };

  // Lade Termine aus dem localStorage
  useEffect(() => {
    try {
      // Versuche, Termine aus dem localStorage zu laden
      const storedTermine = localStorage.getItem('oetterliapps_termine');
      let terminData: Termin[] = [];
      
      if (storedTermine) {
        // Wenn Termine vorhanden sind, parse sie
        terminData = JSON.parse(storedTermine);
        console.log('Termine aus localStorage geladen:', terminData.length);
      } else {
        console.log('Keine Termine im localStorage gefunden');
      }
      
      // Fallback zu Beispieldaten, wenn keine Termine im localStorage sind
      if (terminData.length === 0) {
        terminData = [
          {
            id: 'beispiel_1',
            email: 'max@example.com',
            phone: '+41 76 123 45 67',
            topic: 'prozess-optimierung',
            date: '2025-06-10',
            createdAt: '2025-06-01T10:30:00',
            status: 'neu'
          },
          {
            id: 'beispiel_2',
            email: 'anna@example.com',
            topic: 'app-entwicklung',
            date: '2025-06-15',
            createdAt: '2025-06-02T14:15:00',
            status: 'bestätigt'
          }
        ];
        console.log('Beispieldaten geladen, da keine echten Termine vorhanden sind');
      }
      
      // Sortiere Termine nach Erstellungsdatum (neueste zuerst)
      terminData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      setTermine(terminData);
      setFilteredTermine(terminData);
      setIsLoading(false);
      
      // Statistiken berechnen
      updateStats(terminData);
    } catch (error) {
      console.error('Fehler beim Laden der Termine aus dem localStorage:', error);
      setIsLoading(false);
      
      // Leere Arrays setzen im Fehlerfall
      setTermine([]);
      setFilteredTermine([]);
      updateStats([]);
    }
  }, []);

  // Statistiken aktualisieren
  const updateStats = (data: Termin[]) => {
    const stats = {
      total: data.length,
      neu: data.filter(t => t.status === 'neu').length,
      bestätigt: data.filter(t => t.status === 'bestätigt').length,
      abgeschlossen: data.filter(t => t.status === 'abgeschlossen').length,
      storniert: data.filter(t => t.status === 'storniert').length,
    };
    setStats(stats);
  };

  // Filter anwenden
  useEffect(() => {
    let result = [...termine];
    
    // Status Filter
    if (filter.status !== 'alle') {
      result = result.filter(termin => termin.status === filter.status);
    }
    
    // Thema Filter
    if (filter.topic !== 'alle') {
      result = result.filter(termin => termin.topic === filter.topic);
    }
    
    // Datum Filter
    if (filter.startDate) {
      const startDate = new Date(filter.startDate);
      startDate.setHours(0, 0, 0, 0);
      result = result.filter(termin => {
        const terminDate = new Date(termin.date);
        terminDate.setHours(0, 0, 0, 0);
        return terminDate >= startDate;
      });
    }
    
    if (filter.endDate) {
      const endDate = new Date(filter.endDate);
      endDate.setHours(23, 59, 59, 999);
      result = result.filter(termin => {
        const terminDate = new Date(termin.date);
        terminDate.setHours(0, 0, 0, 0);
        return terminDate <= endDate;
      });
    }
    
    // Suchbegriff
    if (filter.searchTerm) {
      const searchTerm = filter.searchTerm.toLowerCase();
      result = result.filter(
        termin =>
          termin.email.toLowerCase().includes(searchTerm) ||
          (termin.phone && termin.phone.toLowerCase().includes(searchTerm))
      );
    }
    
    setFilteredTermine(result);
  }, [termine, filter]);

  // Status eines Termins ändern
  const updateTerminStatus = (id: string, newStatus: Termin['status']) => {
    const updatedTermine = termine.map(termin => 
      termin.id === id ? { ...termin, status: newStatus } : termin
    );
    
    // Aktualisiere den Status im State
    setTermine(updatedTermine);
    setFilteredTermine(prev => prev.map(termin => 
      termin.id === id ? { ...termin, status: newStatus } : termin
    ));
    
    // Aktualisiere die Statistiken
    updateStats(updatedTermine);
    
    // Speichere die aktualisierten Termine im localStorage
    try {
      localStorage.setItem('oetterliapps_termine', JSON.stringify(updatedTermine));
      console.log(`Termin ${id} Status geändert zu ${newStatus} und im localStorage gespeichert`);
    } catch (error) {
      console.error('Fehler beim Speichern der Termine im localStorage:', error);
    }
  };

  // CSV Export
  const exportToCSV = () => {
    const headers = ['ID', 'E-Mail', 'Telefon', 'Thema', 'Datum', 'Erstellt am', 'Status'];
    
    const csvContent = [
      headers.join(','),
      ...filteredTermine.map(termin => [
        termin.id,
        termin.email,
        termin.phone || '',
        termin.topic,
        termin.date,
        termin.createdAt,
        termin.status
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `termine_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Formatierung des Themas für die Anzeige
  const formatTopic = (topic: string) => {
    const topicMap: Record<string, string> = {
      'prozess-optimierung': 'Prozessoptimierung',
      'app-entwicklung': 'App-Entwicklung',
      'marketing-automation': 'Marketing-Automatisierung',
      'crm-optimierung': 'CRM & Vertrieb',
      'other': 'Anderes Thema'
    };
    return topicMap[topic] || topic;
  };

  // Formatierung des Datums für die Anzeige
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  // Formatierung des Zeitstempels für die Anzeige
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.toLocaleDateString('de-DE')} ${date.toLocaleTimeString('de-DE')}`;
  };

  // Status-Badge Komponente
  const StatusBadge: React.FC<{ status: Termin['status'] }> = ({ status }) => {
    const statusConfig = {
      neu: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Neu' },
      bestätigt: { bg: 'bg-green-100', text: 'text-green-800', label: 'Bestätigt' },
      abgeschlossen: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Abgeschlossen' },
      storniert: { bg: 'bg-red-100', text: 'text-red-800', label: 'Storniert' }
    };
    
    const config = statusConfig[status];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-20 mt-16">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-sky-800 mb-4 md:mb-0">Terminverwaltung</h1>
          <Button 
            variant="secondary" 
            size="md" 
            onClick={exportToCSV}
            className="flex items-center"
          >
            <Download className="mr-2 h-4 w-4" />
            CSV Export
          </Button>
        </div>
        
        {/* Statistik-Karten */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gesamt</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-full">
                <Calendar className="h-6 w-6 text-gray-700" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Neu</p>
                <p className="text-2xl font-bold text-blue-600">{stats.neu}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Calendar className="h-6 w-6 text-blue-700" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Bestätigt</p>
                <p className="text-2xl font-bold text-green-600">{stats.bestätigt}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Calendar className="h-6 w-6 text-green-700" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Abgeschlossen</p>
                <p className="text-2xl font-bold text-purple-600">{stats.abgeschlossen}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Calendar className="h-6 w-6 text-purple-700" />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Storniert</p>
                <p className="text-2xl font-bold text-red-600">{stats.storniert}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <Calendar className="h-6 w-6 text-red-700" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Filter-Bereich */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <div className="flex items-center mb-4">
            <Filter className="h-5 w-5 text-gray-500 mr-2" />
            <h2 className="text-lg font-semibold">Filter</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status-filter"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={filter.status}
                onChange={(e) => setFilter({...filter, status: e.target.value})}
              >
                <option value="alle">Alle Status</option>
                <option value="neu">Neu</option>
                <option value="bestätigt">Bestätigt</option>
                <option value="abgeschlossen">Abgeschlossen</option>
                <option value="storniert">Storniert</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="topic-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Thema
              </label>
              <select
                id="topic-filter"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={filter.topic}
                onChange={(e) => setFilter({...filter, topic: e.target.value})}
              >
                <option value="alle">Alle Themen</option>
                <option value="prozess-optimierung">Prozessoptimierung</option>
                <option value="app-entwicklung">App-Entwicklung</option>
                <option value="marketing-automation">Marketing-Automatisierung</option>
                <option value="crm-optimierung">CRM & Vertrieb</option>
                <option value="other">Anderes Thema</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
                Von Datum
              </label>
              <input
                type="date"
                id="start-date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={filter.startDate}
                onChange={(e) => setFilter({...filter, startDate: e.target.value})}
              />
            </div>
            
            <div>
              <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
                Bis Datum
              </label>
              <input
                type="date"
                id="end-date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={filter.endDate}
                onChange={(e) => setFilter({...filter, endDate: e.target.value})}
              />
            </div>
            
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Suche
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="E-Mail, Telefon..."
                  value={filter.searchTerm}
                  onChange={(e) => setFilter({...filter, searchTerm: e.target.value})}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Tabelle */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            {isLoading ? (
              <motion.div 
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sky-500 border-t-transparent"></div>
                <p className="mt-2 text-gray-600">Termine werden geladen...</p>
              </motion.div>
            ) : filteredTermine.length === 0 ? (
              <motion.div 
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="p-8 text-center">
                <p className="text-gray-600">Keine Termine gefunden, die den Filterkriterien entsprechen.</p>
              </motion.div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kontakt
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Thema
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Datum
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Erstellt am
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTermine.map((termin) => (
                    <motion.tr 
                      key={termin.id} 
                      className="hover:bg-gray-50"
                      initial="hidden"
                      animate="visible"
                      variants={itemVariants}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{termin.email}</div>
                        {termin.phone && <div className="text-sm text-gray-500">{termin.phone}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatTopic(termin.topic)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(termin.date)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={termin.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{formatTimestamp(termin.createdAt)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {termin.status === 'neu' && (
                            <button
                              onClick={() => updateTerminStatus(termin.id, 'bestätigt')}
                              className="text-green-600 hover:text-green-900"
                            >
                              Bestätigen
                            </button>
                          )}
                          {termin.status === 'bestätigt' && (
                            <button
                              onClick={() => updateTerminStatus(termin.id, 'abgeschlossen')}
                              className="text-purple-600 hover:text-purple-900"
                            >
                              Abschließen
                            </button>
                          )}
                          {(termin.status === 'neu' || termin.status === 'bestätigt') && (
                            <button
                              onClick={() => updateTerminStatus(termin.id, 'storniert')}
                              className="text-red-600 hover:text-red-900"
                            >
                              Stornieren
                            </button>
                          )}
                          {(termin.status === 'abgeschlossen' || termin.status === 'storniert') && (
                            <button
                              onClick={() => updateTerminStatus(termin.id, 'neu')}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Zurücksetzen
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        
        <div className="mt-6 text-sm text-gray-500">
          <p>Hinweis: Diese Seite zeigt alle beantragten Termine und deren Status. Sie können die Termine nach verschiedenen Kriterien filtern und den Status der Termine ändern.</p>
        </div>
      </div>
    </Layout>
  );
};

export default TerminVerwaltung;
